<template>
  <div class="entry-overview flex overflow-hidden bg-white shadow sm:rounded-lg">
    <div class="entry-detail w-full">
      <div class="flex flex-col">
        <!-- Header -->
        <DestinationHeader
          :destination="destination"
          :button="false" />
        <!-- Main content -->
        <main class="flex flex-row">
          <!-- Destination information registered -->
          <div
            class="basis-1/2 2xl:basis-2/5">
            <EntityInfo
              :key="'destinationInfo-' + destination.id"
              :entity="destination"
              :current-user="currentUser"
              :has-heading="{active: true, class: 'text-xl font-medium px-2 py-1 placeholder:italic'}"
              :has-proposal-program-text="{active: false}"
              class="size-full"
              v-bind="$attrs" />
          </div>
          <!-- Destination map, entities and proposals overview -->
          <div class="relative basis-1/2 2xl:basis-3/5 flex-col border-l border-gray-200">
            <!-- Map of destination -->
            <div class="relative flex aspect-[16/9] max-h-[50vh] w-full flex-row">
              <GMapMap
                class="relative h-full w-1/2 2xl:w-2/3"
                :center="destinationLocation"
                :zoom="5"
                map-type-id="roadmap"
                :options="{
                  zoomControl: true,
                  mapTypeControl: false,
                  scaleControl: false,
                  streetViewControl: false,
                  rotateControl: false,
                  fullscreenControl: false,
                }"
                style="position: absolute; width: 100%; height: 100%;">
                <GMapMarker
                  :position="destinationLocation" />
              </GMapMap>
              <EntityNote
                class="relative h-full w-1/2 2xl:w-1/3"
                :entity="destination"
                :route-prop="route('api.destination.note.store', { place: destination.id })"
                fill-space />
            </div>
            <!-- Entities and proposals -->
            <div class="border-t border-gray-100" />
            <div class="flex flex-row border-t border-gray-100 pb-6 pt-4">
              <!-- Entities overview -->
              <div class="basis-1/2 border-r border-gray-200 px-5">
                <h2 class="text-lg font-medium">
                  {{ destination.entities.length }} entities in database
                </h2>
                <Link :href="route('destinations.map', destination.id)">
                  <div class="mt-4 grid grid-cols-1 gap-3">
                    <!-- Lodges -->
                    <div class="flex items-center">
                      <HomeIcon
                        class="size-12 rounded-full border border-gray-300 p-2 pb-2.5 text-teal-600"
                        aria-hidden="true" />
                      <span class="ml-2 text-gray-800">{{ entityCounter.lodges }} Lodges</span>
                    </div>
                    <!-- Eateries -->
                    <div class="flex items-center">
                      <CakeIcon
                        class="size-12 rounded-full border border-gray-300 p-2 pb-2.5 text-teal-600"
                        aria-hidden="true" />
                      <span class="ml-2 text-gray-800">{{ entityCounter.eateries }} Eateries</span>
                    </div>
                    <!-- Activities -->
                    <div class="flex items-center">
                      <MapIcon
                        class="size-12 rounded-full border border-gray-300 p-2 pb-2.5 text-teal-600"
                        aria-hidden="true" />
                      <span class="ml-2 text-gray-800">{{ entityCounter.activities }} Activities</span>
                    </div>
                    <!-- Venues -->
                    <div class="flex items-center">
                      <BuildingOfficeIcon
                        class="size-12 rounded-full border border-gray-300 p-2 pb-2.5 text-teal-600"
                        aria-hidden="true" />
                      <span class="ml-2 text-gray-800">{{ entityCounter.venues }} Venues</span>
                    </div>
                    <!-- Landmarks & POI -->
                    <div class="flex items-center">
                      <BuildingLibraryIcon
                        class="size-12 rounded-full border border-gray-300 p-2 pb-2.5 text-teal-600"
                        aria-hidden="true" />
                      <span class="ml-2 text-gray-800">{{ entityCounter.landmarks }} Landmarks & POI</span>
                    </div>
                    <!-- Others -->
                    <div class="flex items-center">
                      <MapPinIcon
                        class="size-12 rounded-full border border-gray-300 p-2 pb-2.5 text-teal-600"
                        aria-hidden="true" />
                      <span class="ml-2 text-gray-800">{{ entityCounter.others }} Others</span>
                    </div>
                  </div>
                </Link>
              </div>
              <!-- Proposals overview -->
              <div class="basis-1/2 px-5">
                <h2 class="text-lg font-medium">
                  {{ destination.proposals.length }} proposals from {{ destination.name }}
                </h2>
                <div
                  v-if="destination.proposals.length > 0"
                  class="mt-4 grid grid-cols-1 gap-2">
                  <div
                    v-for="(proposal, index) in proposalsByNewestStartDateLimited"
                    :key="index"
                    class="border-b border-gray-200 pb-3 last-of-type:border-none">
                    <h3 class="text-sm font-semibold text-gray-800">
                      {{ proposal.title }} <span
                        class="text-xs font-medium text-gray-500"
                        title="Travel start date, month/year">({{ proposal.startMonthYear }})</span>
                    </h3>
                    <p class="text-xs font-light text-gray-400">
                      {{ proposal.client.name }} - {{ proposal.days }} days
                    </p>
                    <p class="mt-1 text-xs font-normal text-gray-800">
                      {{ proposal.entityCount }} entities, {{ proposal.pax }} pax of {{ proposal.amount_pr_pax }},-
                    </p>
                  </div>
                  <Link
                    class="mt-2 text-center text-xs underline hover:text-purple-800"
                    :href="route('destinations.proposals', destination)">
                    See all proposals
                  </Link>
                </div>
                <div
                  v-else
                  class="mt-4">
                  <p class="text-sm text-gray-800">
                    No proposals from this destination yet.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  </div>
</template>

<script setup>
/* external */
import {
  computed, useAttrs,
} from 'vue';
import { Link } from '@inertiajs/vue3';
import moment from 'moment-timezone';
import {
  CakeIcon,
  HomeIcon,
  BuildingLibraryIcon,
  MapPinIcon,
  MapIcon,
  BuildingOfficeIcon,
} from '@heroicons/vue/24/outline';

/* local */
import EntityInfo from '@/Pages/Destination/Components/EntityInfo.vue';
import EntityNote from '@/Pages/Destination/Components/EntityNote.vue';
import { getDateTimeInDestinationTimezone } from '@/utils';
import DestinationHeader from '@/Pages/Destination/Components/DestinationHeader';

const props = defineProps({
  destination: {
    type: Object,
    required: true,
    default: () => ({}),
  },
});

const attrs = useAttrs();
const currentUser = attrs.user;

/**
 * Page functions
 * @param {string} startDate - The start date
 * @param {string} endDate - The end date
 * @param {string} timezone - The timezone
 */
function getDaysBetweenDates(startDate, endDate, timezone) {
  const start = moment.tz(startDate, timezone);
  const end = moment.tz(endDate, timezone);

  return end.diff(start, 'days', true);
}

/**
 * Entities handling
 */
const entityCounter = computed(() => {
  const lodges = props.destination.entities.filter((entity) => entity.type === 'lodging').length;
  const eateries = props.destination.entities.filter((entity) => entity.type === 'eatery').length;
  const activities = props.destination.entities.filter((entity) => entity.type === 'activity').length;
  const venues = props.destination.entities.filter((entity) => entity.type === 'venue').length;
  const landmarks = props.destination.entities.filter((entity) => entity.type === 'landmark').length;
  const others = props.destination.entities.filter((entity) => entity.type === 'other').length;

  return {
    lodges,
    eateries,
    activities,
    venues,
    landmarks,
    others,
  };
});

/**
 * Proposals handling
 */
const proposalsByNewestStartDateLimited = computed(() => {
  const { proposals } = props.destination;
  const sortedAndCut = proposals
    .sort((a, b) => {
      const aTime = moment.tz(a.start_date, a.data.destination.timezone).valueOf();
      const bTime = moment.tz(b.start_date, b.data.destination.timezone).valueOf();

      return bTime - aTime;
    })
    .slice(0, 4);

  sortedAndCut.forEach((proposal) => {
    proposal.startMonthYear = getDateTimeInDestinationTimezone(proposal.start_date, proposal.data.destination.timezone, 'MM/YY');
    proposal.days = getDaysBetweenDates(proposal.start_date, proposal.end_date, proposal.data.destination.timezone);
    proposal.entityCount = proposal.itinerary.length;
  });

  return sortedAndCut;
});

/**
 * Map handling
 */
const destinationLocation = computed(() => {
  const { location } = props.destination;

  return {
    lat: location.coordinates[1],
    lng: location.coordinates[0],
  };
});

</script>
